import React, { useState, useEffect, useCallback } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { useDispatch } from 'react-redux';
import { Button, Row, Col } from 'react-bootstrap';
import * as Yup from 'yup';
import Icon from '../../components/Icons';
import { FormikInput, FormikDatePicker, FormikSelect, UploadFile, ButtonTooltip } from '../../components';
import { debounceIndexEmployeesRequest } from '../../requests/employees';

const UnjustifiedAbsenceForm = props => {
  const { onHide, errors, touched, setFieldValue, isSubmitting, mode = 'new', unjustifiedAbsence, values } = props;

  const dispatch = useDispatch();
  const isReadOnly = mode === 'show';
  const [employeesFiltered, setEmployeesFiltered] = useState([]);
  const [defaultEmployees, setDefaultEmployees] = useState([]);
  const { fileInfo } = unjustifiedAbsence;

  const fetchEmployees = useCallback(() => {
    debounceIndexEmployeesRequest({
      dispatch,
      params: {
        is_dt: false,
        active: true,
        sort_column: 'name',
        paginate: false
      },
      successCallback: response => {
        const result = response.data.data.map(emp => ({
          ...emp,
          label: emp.short_name || emp.name,
          value: emp.id
        }));
        setEmployeesFiltered(result);
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (isReadOnly && unjustifiedAbsence?.employee) {
      setDefaultEmployees([
        {
          label: unjustifiedAbsence.employee.fullName || unjustifiedAbsence.employee.name,
          value: unjustifiedAbsence.employee.id
        }
      ]);
    } else {
      fetchEmployees();
    }
  }, [isReadOnly, unjustifiedAbsence, fetchEmployees]);

  const handleMultiChange = (data, fieldName) => {
    setFieldValue(fieldName, data || []);
    setDefaultEmployees(data || []);
  };

  return (
    <Form>
      <Row className="mb-3">
        <Col md={12}>
          <Field name="unjustifiedAbsence[employeeIds]">
            {({ field }) => (
              <FormikSelect
                {...field}
                isMulti
                abbr
                label="Colaboradores"
                placeholder="Seleccionar colaboradores"
                options={employeesFiltered}
                defaultMultiValues={defaultEmployees}
                value={defaultEmployees}
                onChange={data => handleMultiChange(data, field.name)}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
                isDisabled={isReadOnly}
              />
            )}
          </Field>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Field name="unjustifiedAbsence[date]">
            {({ field }) => (
              <FormikDatePicker
                {...field}
                abbr
                isOutsideRange={() => false}
                label="Fecha"
                placeholder="dd/mm/aaaa"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
                disabled={isReadOnly}
              />
            )}
          </Field>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Field name="unjustifiedAbsence[reason]">
            {({ field }) => (
              <FormikInput
                {...field}
                abbr
                as="textarea"
                label="Motivo"
                rows={5}
                maxLength={500}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
                disabled={isReadOnly}
                readOnly={isReadOnly}
              />
            )}
          </Field>
        </Col>
      </Row>

      {!isReadOnly && (
        <Row className="mb-3">
          <Col md={6}>
            <Field name="unjustifiedAbsence[file]">
              {({ field }) => (
                <UploadFile
                  name="Adjuntar Archivo"
                  onChange={file => {
                    setFieldValue(field.name, file);
                  }}
                  error={getIn(errors, field.name)}
                  value={values.unjustifiedAbsence.file}
                  touched={getIn(touched, field.name)}
                  disabled={isReadOnly}
                />
              )}
            </Field>
          </Col>
        </Row>
      )}

      <Row xs="auto">
        {fileInfo && Object.keys(fileInfo).length > 0 && (
          <>
            <Col xs={1}>
              <ButtonTooltip
                onClick={() => window.open(fileInfo.fileUrl, '_blank', 'noopener', 'noreferrer')}
                variant="circle-primary"
                className="btn-circle mx-0"
                text="Descargar"
              >
                <Icon icon="download-2" height="16px" width="16px" />
              </ButtonTooltip>
            </Col>
            <Col xs={6} className="d-flex align-items-center">
              <p className="mb-0">Descargar Documento Adjunto</p>
            </Col>
          </>
        )}
      </Row>

      <Row className="d-flex justify-content-end">
        <Col md={3} className="mb-2">
          <Button block variant="outline-info" onClick={onHide}>
            {isReadOnly ? 'Cerrar' : 'Cancelar'}
          </Button>
        </Col>
        {!isReadOnly && (
          <Col md={3} className="mb-2">
            <Button block type="submit" variant="primary" disabled={isSubmitting}>
              Crear
            </Button>
          </Col>
        )}
      </Row>
    </Form>
  );
};

const setInitialValues = ({ unjustifiedAbsence = {} }) => {
  const { employeeIds = [], date = '', reason = '', file = null } = unjustifiedAbsence;

  return {
    unjustifiedAbsence: {
      employeeIds,
      date,
      reason,
      file
    }
  };
};

const validationSchema = Yup.object().shape({
  unjustifiedAbsence: Yup.object().shape({
    employeeIds: Yup.array()
      .min(1, 'Debe seleccionar al menos un colaborador')
      .required('Requerido'),
    date: Yup.date()
      .required('Requerido')
      .formatdate(),
    reason: Yup.string()
      .min(5, 'Mínimo 5 caracteres')
      .max(500, 'Máximo 500 caracteres')
      .required('Requerido'),
    file: Yup.mixed()
  })
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest, mode } = props;
  if (mode === 'show') return setSubmitting(false);

  const customValues = {
    unjustifiedAbsence: {
      ...values.unjustifiedAbsence,
      employeeIds: values.unjustifiedAbsence.employeeIds.map(e => e.value)
    }
  };

  return formRequest(customValues, setSubmitting);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: false
})(UnjustifiedAbsenceForm);
